/**
 * OpenTelemetry browser instrumentation for sq-backoffice-desktop-client.
 *
 * Imported at the top of main.ts (before bootstrapApplication) so tracing
 * and auto-instrumentation are active from the very first HTTP call.
 *
 * Sends:
 *   - Traces → Alloy OTLP HTTP endpoint
 *   - Logs (console.error bridge) → Alloy OTLP HTTP endpoint
 *
 * Browser → Alloy endpoint:
 *   Local dev:   http://localhost:4318  (Alloy port-forwarded to host)
 *   Production:  https://observability.staqable.com:4318
 */

import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { SeverityNumber } from '@opentelemetry/api-logs';
import { Resource } from '@opentelemetry/resources';
import {
  SEMRESATTRS_SERVICE_NAME,
  SEMRESATTRS_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { environment } from './environments/environment';

const ALLOY_ENDPOINT = environment.production
  ? 'https://observability.staqable.com:4318'
  : 'http://localhost:4318';

const resource = new Resource({
  [SEMRESATTRS_SERVICE_NAME]: 'sq-backoffice-client',
  [SEMRESATTRS_SERVICE_VERSION]: '1.0.0',
  'deployment.environment': environment.production ? 'production' : 'development',
});

// ── Traces ──────────────────────────────────────────────────────────────────
const traceProvider = new WebTracerProvider({ resource });
traceProvider.addSpanProcessor(
  new BatchSpanProcessor(
    new OTLPTraceExporter({ url: `${ALLOY_ENDPOINT}/v1/traces` }),
  ),
);
traceProvider.register({ propagator: new W3CTraceContextPropagator() });

// ── Logs ────────────────────────────────────────────────────────────────────
const loggerProvider = new LoggerProvider({ resource });
loggerProvider.addLogRecordProcessor(
  new BatchLogRecordProcessor(
    new OTLPLogExporter({ url: `${ALLOY_ENDPOINT}/v1/logs` }),
  ),
);

// ── Auto-instrumentation ────────────────────────────────────────────────────
registerInstrumentations({
  instrumentations: [
    new FetchInstrumentation({
      // Propagate W3C traceparent header on all API calls so browser spans link to backend spans
      propagateTraceHeaderCorsUrls: [/staqable\.com/, /localhost/],
      clearTimingResources: true,
    }),
    new XMLHttpRequestInstrumentation({
      propagateTraceHeaderCorsUrls: [/staqable\.com/, /localhost/],
    }),
    new DocumentLoadInstrumentation(),
    new UserInteractionInstrumentation(),
  ],
});

// ── Console error bridge → OTEL logs ────────────────────────────────────────
// Intercepts console.error so JS errors appear as structured log records in Loki.
// OtelErrorHandlerService emits directly and calls _origError — bridge is skipped
// via _fromErrorHandler flag to prevent double-emission.
export const otelLogger = loggerProvider.getLogger('browser-console');
export const _origError = console.error.bind(console);
let _fromErrorHandler = false;

export function setFromErrorHandler(value: boolean): void {
  _fromErrorHandler = value;
}

function serializeArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg instanceof Error) return arg.message;
      if (typeof arg === 'object' && arg !== null) {
        try { return JSON.stringify(arg); } catch { return String(arg); }
      }
      return String(arg);
    })
    .join(' ');
}

console.error = (...args: unknown[]): void => {
  _origError(...args);
  if (_fromErrorHandler) return;
  const firstArg = args[0];
  otelLogger.emit({
    severityNumber: SeverityNumber.ERROR,
    severityText: 'ERROR',
    body: serializeArgs(args),
    attributes: {
      'browser.user_agent': navigator.userAgent,
      'url': window.location.href,
      ...(firstArg instanceof Error && {
        'error.type': firstArg.name,
        'error.stack': firstArg.stack ?? '',
      }),
    },
  });
};

// ── Unhandled promise rejections ─────────────────────────────────────────────
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  otelLogger.emit({
    severityNumber: SeverityNumber.ERROR,
    severityText: 'ERROR',
    body: reason instanceof Error ? reason.message : String(reason),
    attributes: {
      'error.type': reason instanceof Error ? reason.name : 'UnhandledRejection',
      'error.stack': reason instanceof Error ? (reason.stack ?? '') : '',
      'browser.user_agent': navigator.userAgent,
      'url': window.location.href,
    },
  });
});

export { traceProvider, loggerProvider };
